import { memo, useEffect, useRef } from "react";
import { useScroll, useMotionValue } from "motion/react";

export const GradientShader = memo(() => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { scrollYProgress } = useScroll();
  const scrollDepth = useMotionValue(0);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      scrollDepth.set(latest);
    });
    return unsubscribe;
  }, [scrollYProgress, scrollDepth]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl") as WebGLRenderingContext | null;
    if (!gl) return;

    const vertexShaderSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragmentShaderSource = `
      precision mediump float;
      uniform vec2 resolution;
      uniform float time;
      uniform float scrollDepth;

      void main() {
        vec2 uv = gl_FragCoord.xy / resolution;
        
        float t = time * 0.8;
        
        vec2 center1 = vec2(0.5 + sin(t * 0.5) * 0.25, 0.8 + cos(t * 0.3) * 0.18);
        vec2 center2 = vec2(0.85 + sin(t * 0.7) * 0.2, 0.5 + cos(t * 0.5) * 0.25);
        vec2 center3 = vec2(0.15 + cos(t * 0.4) * 0.22, 0.3 + sin(t * 0.6) * 0.18);
        vec2 center4 = vec2(0.5 + cos(t * 0.6) * 0.18, 0.2 + sin(t * 0.4) * 0.22);
        vec2 center5 = vec2(0.75 + sin(t * 0.8) * 0.15, 0.1 + cos(t * 0.7) * 0.15);
        
        float dist1 = length(uv - center1);
        float dist2 = length(uv - center2);
        float dist3 = length(uv - center3);
        float dist4 = length(uv - center4);
        float dist5 = length(uv - center5);
        
        vec3 lightColor1 = vec3(0.15, 0.38, 0.65);
        vec3 lightColor2 = vec3(0.28, 0.52, 0.75);
        vec3 lightColor3 = vec3(0.12, 0.35, 0.6);
        
        vec3 darkColor1 = vec3(0.05, 0.2, 0.45);
        vec3 darkColor2 = vec3(0.08, 0.3, 0.55);
        vec3 darkColor3 = vec3(0.03, 0.18, 0.4);
        
        vec3 color1 = mix(lightColor1, darkColor1, scrollDepth);
        vec3 color2 = mix(lightColor2, darkColor2, scrollDepth);
        vec3 color3 = mix(lightColor3, darkColor3, scrollDepth);
        
        float blur1 = smoothstep(0.8, 0.0, dist1);
        float blur2 = smoothstep(0.6, 0.0, dist2);
        float blur3 = smoothstep(0.7, 0.0, dist3);
        float blur4 = smoothstep(0.5, 0.0, dist4);
        float blur5 = smoothstep(0.5, 0.0, dist5);
        
        vec3 baseColor = vec3(0.01, 0.08, 0.2);
        vec3 finalColor = mix(baseColor, color1, blur1 * 0.8);
        finalColor = mix(finalColor, color2, blur2 * 0.7);
        finalColor = mix(finalColor, color3, blur3 * 0.6);
        finalColor = mix(finalColor, color1, blur4 * 0.5);
        finalColor = mix(finalColor, color2, blur5 * 0.5);
        
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `;

    const createShader = (type: number, source: string): WebGLShader | null => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return shader;
    };

    const vertexShader = createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(
      gl.FRAGMENT_SHADER,
      fragmentShaderSource,
    );

    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    if (!program) return;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const resolutionLocation = gl.getUniformLocation(program, "resolution");
    const timeLocation = gl.getUniformLocation(program, "time");
    const scrollDepthLocation = gl.getUniformLocation(program, "scrollDepth");

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      gl.viewport(0, 0, canvas.width, canvas.height);
      if (resolutionLocation) {
        gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      }
    };

    resize();
    window.addEventListener("resize", resize);

    let animationId: number;
    const startTime = Date.now();

    const render = () => {
      const elapsed = (Date.now() - startTime) / 1000;

      if (timeLocation) {
        gl.uniform1f(timeLocation, elapsed);
      }

      if (scrollDepthLocation) {
        gl.uniform1f(scrollDepthLocation, scrollDepth.get());
      }

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      animationId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationId);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      gl.deleteBuffer(buffer);
    };
  }, [scrollDepth]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed left-0 top-0 w-full min-h-[120vh] h-[120vh] -z-10 pointer-events-none"
    />
  );
});

export const Gradients = GradientShader;
